<template>
  <div class="mobile-page">
    <!-- 搜索输入区域 -->
    <div class="search-barcode-wrap">
      <div class="search-input-box">
        <el-input
          v-model="searchKeyword"
          placeholder="输入商品条码，或点击扫码"
          @input="handleSearchInput"
          clearable
        />
        <button class="scan-btn" @click="openScanner">扫码</button>
      </div>
    </div>

    <!-- 扫码弹窗 -->
    <div v-if="showScanner" class="scan-modal">
      <div id="reader" class="reader-view"></div>
      
      <!-- 自定义视觉引导框（仅做提示，不限制真实识别区） -->
      <div class="scan-mask">
        <div class="scan-frame">
          <div class="scan-line"></div>
        </div>
        <div class="scan-tip">将条形码横向对准框内</div>
        <div class="scan-btns">
          <button @click="closeScanner">关闭扫码</button>
        </div>
      </div>
    </div>

    <!-- 商品列表 -->
    <div class="product-list">
      <div v-for="product in goods" :key="product.id" class="product-card">
        <div class="product-info">
          <h3>{{ product.name }}</h3>
          <p class="specs">规格：{{ product.form }}</p >
          <p class="price">售价：¥{{ product.price }}</p >
          <p class="price">¥{{ product.bidPrice }}</p >
        </div>
      </div>
    </div>
    
    <!-- 空状态 -->
    <div v-if="goods.length === 0 && !loading" class="empty-tip">
      暂无相关商品
    </div>

    <!-- 分页器 -->
    <div class="pagination">
      <button @click="prevPage" :disabled="page === 1 || loading">上一页</button>
      <span>第 {{ page }} / {{ total }} 页</span>
      <button @click="nextPage" :disabled="page === total || loading">下一页</button>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode'
import { getGoodsList } from '../../api/api'

export default {
  name: 'GoodsScanner',
  data() {
    return {
      goods: [],
      total: 0,
      page: 1,
      pageSize: 6,
      searchKeyword: '',
      loading: false,

      showScanner: false,
      scanner: null,
      scanLock: false,
      debounceSearch: null
    }
  },
  created() {
    this.debounceSearch = _.debounce(this.getGoods, 300)
  },
  beforeDestroy() {
    this.closeScanner()
  },
  methods: {
    handleSearchInput() {
      this.page = 1
      this.debounceSearch()
    },

    async openScanner() {
      this.showScanner = true
      this.scanLock = false
      
      await this.$nextTick()
      
      // 防止重复初始化
      if (this.scanner) {
        try { await this.scanner.stop() } catch (e) {}
        this.scanner = null
      }

      try {
        this.scanner = new Html5Qrcode('reader')

        const config = {
          fps: 18,

          formatsToSupport: [
              Html5QrcodeSupportedFormats.CODE_128,
              Html5QrcodeSupportedFormats.EAN_13,
              Html5QrcodeSupportedFormats.EAN_8
          ],
          aspectRatio:1.8,
          useBarCodeDetectorIfSupported: true,
          videoConstraints: {
            // 强制高像素
            width: { ideal: 1440, min: 1080 },
            height: { ideal: 2560, min: 1920 },
            focusMode: "continuous", // 持续自动对焦
            // 部分手机支持降噪
            advanced: [
              { noiseReduction: { ideal: true } },
              { zoom: 1.6 },
              { focusMode: "continuous" }, // 持续自动对焦
              { exposureMode: "continuous" }, // 持续曝光
              { whiteBalanceMode: "continuous" } // 自动白平衡
            ],
          }
        }

        await this.scanner.start(
          { facingMode: 'environment' },
          config,
          this.onScanSuccess,
          (error) => {
            // 调试时可打开，查看识别日志
            // console.log('解码中：', error)
          }
        )
      } catch (error) {
        console.error('扫码启动失败：', error)
        this.$message.error('摄像头调用失败，请检查权限与网络环境')
        this.closeScanner()
      }
    },

    onScanSuccess(decodedText) {
      if (this.scanLock) return
      this.scanLock = true

      // 震动反馈，确认识别成功
      if (navigator.vibrate) navigator.vibrate(100)

      this.searchKeyword = decodedText
      this.page = 1
      this.getGoods()
      this.closeScanner()

      setTimeout(() => {
        this.scanLock = false
      }, 1500)
    },

    async closeScanner() {
      if (this.scanner) {
        try {
          await this.scanner.stop()
          this.scanner.clear()
        } catch (e) {}
        this.scanner = null
      }
      this.showScanner = false
      this.scanLock = false
    },

    getGoods() {
      this.loading = true
      const params = {
        page: this.page,
        size: this.pageSize,
        name: this.searchKeyword
      }

      getGoodsList(params)
        .then(res => {
          const { code, message, data } = res.data
          if (code !== 200) {
            this.$message.error(message)
            this.goods = []
            this.total = 0
          } else {
            this.goods = data.list || []
            this.page = data.pageNum === 0 ? 1 : data.pageNum
            this.total = data.total || 0
          }
        })
        .catch(() => {
          this.$message.error('查询失败，请重试')
        })
        .finally(() => {
          this.loading = false
        })
    },

    prevPage() {
      if (this.page > 1) {
        this.page--
        this.getGoods()
      }
    },

    nextPage() {
      if (this.page < this.total) {
        this.page++
        this.getGoods()
      }
    }
  },
  mounted() {
    this.getGoods()
  }
}
</script>

<style scoped>
.mobile-page {
  width: 100vw;
  min-height: 100vh;
  max-width: 768px;
  margin: 0 auto;
  padding: 16px;
  box-sizing: border-box;
  background: #f5f5f5;
}

.search-barcode-wrap {
  margin-bottom: 16px;
}
.search-input-box {
  display: flex;
  gap: 8px;
  align-items: center;
}
.scan-btn {
  flex-shrink: 0;
  padding: 0 16px;
  height: 40px;
  border: none;
  border-radius: 6px;
  background: #409eff;
  color: #fff;
  cursor: pointer;
}

.scan-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #000;
  z-index: 999;
  overflow: hidden;
}

.reader-view {
  width: 100%;
  height: 100%;
}

.scan-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.scan-frame {
  position: relative;
  width: 85%;
  max-width: 500px;
  height: 140px;
  border: 2px solid #409eff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
}
.scan-line {
  position: absolute;
  left: 0;
  width: 100%;
  height: 2px;
  background: #409eff;
  animation: scanMove 2s linear infinite;
}
@keyframes scanMove {
  0% { top: 0; }
  50% { top: calc(100% - 2px); }
  100% { top: 0; }
}
.scan-tip {
  margin-top: 24px;
  color: #fff;
  font-size: 14px;
  text-align: center;
}

.scan-btns {
  position: absolute;
  bottom: 60px;
  left: 0;
  width: 100%;
  text-align: center;
}
.scan-btns button {
  padding: 10px 32px;
  border: 1px solid #fff;
  border-radius: 20px;
  background: transparent;
  color: #fff;
  font-size: 16px;
  pointer-events: auto;
  cursor: pointer;
}

.product-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.product-card {
  padding: 16px;
  background: #fff;
  border-radius: 8px;
}

.product-info h3 {
  font-size: 48px;
  margin: 0 0 8px 0;
  color: #333;
}
.product-info .price {
  color: #e4393c;
  font-weight: bold;
  font-size: 48px;
  margin: 0 0 8px 0;
}
.product-info .specs {
  font-size: 36px;
  color: #888;
  margin: 0;
}

.empty-tip {
  padding: 60px 0;
  text-align: center;
  color: #909399;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
  padding: 10px 0;
}
.pagination button {
  padding: 6px 16px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}
.pagination button:disabled {
  color: #c0c4cc;
  cursor: not-allowed;
}
.pagination span {
  font-size: 14px;
  color: #606266;
}
</style>

<!-- 全局样式：强制隐藏库自带的识别框 -->
<style>
#reader #html5-qrcode-scan-region {
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}
#reader video {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
  background: #000;
}
</style>